import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { api } from "../lib/api.js";
import { mediaUrl, preparePhoto } from "../lib/photo.js";

export async function uploadPhoto(
  file: File,
  activityTypeId: string,
  existingLogId?: string,
): Promise<{ photoKey: string; logId: string }> {
  const { blob, contentType } = await preparePhoto(file);
  const presign = await api<{ uploadUrl: string; photoKey: string; logId: string }>(
    "/uploads/presign",
    {
      method: "POST",
      json: {
        activityTypeId,
        contentType,
        contentLength: blob.size,
        logId: existingLogId,
      },
    },
  );
  const put = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: blob,
  });
  if (!put.ok) throw new Error("Photo upload failed. Try again.");
  return { photoKey: presign.photoKey, logId: presign.logId };
}

export function PhotoField({
  activityTypeId,
  photoKey,
  onChange,
  disabled,
}: {
  activityTypeId: string;
  photoKey: string | undefined;
  onChange: (next: { photoKey: string; logId: string } | undefined) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadPhoto(file, activityTypeId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't attach that photo.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
        Photo (optional)
      </span>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        capture="environment"
        className="sr-only"
        disabled={disabled || busy}
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
      {photoKey ? (
        <div className="flex items-start gap-2">
          <img
            src={mediaUrl(photoKey)}
            alt=""
            className="h-20 w-20 rounded-lg object-cover"
          />
          <button
            type="button"
            onClick={() => onChange(undefined)}
            disabled={disabled || busy}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-800"
            aria-label="Remove photo"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
        >
          <Camera size={16} />
          {busy ? "Uploading…" : "Add photo"}
        </button>
      )}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export function PhotoThumb({
  photoKey,
  onOpen,
}: {
  photoKey: string;
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-1 block h-12 w-12 shrink-0 overflow-hidden rounded-md"
      aria-label="View photo"
    >
      <img src={mediaUrl(photoKey)} alt="" className="h-full w-full object-cover" />
    </button>
  );
}

export function PhotoLightbox({ photoKey, onClose }: { photoKey: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <img
        src={mediaUrl(photoKey)}
        alt=""
        className="max-h-full max-w-full rounded-lg object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-gray-900"
        aria-label="Close photo"
      >
        ✕
      </button>
    </div>
  );
}
